import { EpicStatus } from '@modules/private/worklog/data/interfaces/epic.interface'
import { CommercialStatus, ProjectStatus } from '@modules/private/worklog/data/interfaces/project.interface'

// ---- Épicas (vision board + Gantt) ----

export interface EpicCategory {
  id: string
  name: string
  color: string
  icon: string
}

/** Categorías del vision board (orden de secciones). Espeja `EPIC_CATEGORIES` del backend. */
export const EPIC_CATEGORIES: EpicCategory[] = [
  { id: 'saas', name: 'Plataforma SaaS', color: '#10b981', icon: 'fa-solid fa-cloud' },
  { id: 'modules', name: 'Producto · Módulos', color: '#6366f1', icon: 'fa-solid fa-cubes' },
  { id: 'ai', name: 'Inteligencia Artificial', color: '#8B5CF6', icon: 'fa-solid fa-robot' },
  { id: 'engagement', name: 'Engagement', color: '#f59e0b', icon: 'fa-solid fa-trophy' },
  { id: 'mobile', name: 'Mobile', color: '#06b6d4', icon: 'fa-solid fa-mobile-screen-button' },
  { id: 'ops', name: 'Operación', color: '#0ea5e9', icon: 'fa-solid fa-gears' }
]

/**
 * Metadata visual por estado de épica. El estado lo deriva el backend del trabajo real
 * (ADR-0040 · D13), así que estas cinco entradas cubren TODO lo que puede llegar.
 */
export const EPIC_STATUS_META: Record<EpicStatus, {
  label: string
  color: string
  icon: string
}> = {
  achieved:    { label: 'Conseguida',    color: '#00C781', icon: 'fa-solid fa-trophy' },
  in_progress: { label: 'En progreso',   color: '#3B82F6', icon: 'fa-solid fa-bolt' },
  committed:   { label: 'Comprometida',  color: '#f59e0b', icon: 'fa-solid fa-list-check' },
  proposed:    { label: 'Propuesta',     color: '#94a3b8', icon: 'fa-regular fa-lightbulb' },
  blocked:     { label: 'Bloqueada',     color: '#d21313', icon: 'fa-solid fa-ban' }
}

/** Orden de estados dentro de una categoría: lo conseguido arriba, la idea al final. */
export const EPIC_STATUS_ORDER: EpicStatus[] = ['achieved', 'in_progress', 'committed', 'proposed', 'blocked']

/** Color de la barra de una épica en el Gantt: morado de marca, teñido por el estado. */
export const EPIC_BAR_COLOR = '#8B5CF6'

// ---- Proyectos (ADR-0048 · D1) ----

/**
 * Metadata visual por estado de PROYECTO. Lo deriva el backend de sus épicas, así que estas cinco
 * entradas cubren todo lo que puede llegar.
 *
 * `idea` y `scoped` son la diferencia que la pantalla existe para mostrar: uno está registrado y
 * nada más, el otro ya tiene entregables o precio. Antes los dos se veían igual, como «Propuesta».
 */
export const PROJECT_STATUS_META: Record<ProjectStatus, {
  label: string
  color: string
  icon: string
  hint: string
}> = {
  delivered:   { label: 'Entregado',   color: '#00C781', icon: 'fa-solid fa-trophy',        hint: 'Todas sus épicas conseguidas' },
  in_progress: { label: 'En curso',    color: '#3B82F6', icon: 'fa-solid fa-bolt',          hint: 'Con trabajo en el tablero' },
  scoped:      { label: 'Aterrizado',  color: '#f59e0b', icon: 'fa-solid fa-list-check',    hint: 'Ya tiene entregables o precio' },
  idea:        { label: 'Idea',        color: '#94a3b8', icon: 'fa-regular fa-lightbulb',   hint: 'Registrado, sin requerimientos aún' },
  blocked:     { label: 'Bloqueado',   color: '#d21313', icon: 'fa-solid fa-ban',           hint: 'Detenido por algo externo al trabajo' }
}

/** Orden dentro de una categoría: lo que ya corre arriba, la idea al final. */
export const PROJECT_STATUS_ORDER: ProjectStatus[] = ['in_progress', 'scoped', 'delivered', 'idea', 'blocked']

/**
 * Íconos entre los que se elige el de un proyecto. Es una lista corta a propósito: el campo era un
 * input de texto libre donde había que teclear `fa-solid fa-calculator` de memoria, y quien
 * registra un proyecto no tiene por qué saber el catálogo de FontAwesome. Con estos alcanza para
 * distinguir los proyectos de un vistazo; si algún día falta uno, se agrega aquí.
 */
export const PROJECT_ICONS: { icon: string; label: string }[] = [
  { icon: 'fa-solid fa-cube',                 label: 'Módulo' },
  { icon: 'fa-solid fa-calculator',           label: 'Cotización' },
  { icon: 'fa-solid fa-file-contract',        label: 'Contratos' },
  { icon: 'fa-solid fa-money-bill-transfer',  label: 'Pagos' },
  { icon: 'fa-solid fa-file-invoice-dollar',  label: 'Facturación' },
  { icon: 'fa-solid fa-chart-line',           label: 'Métricas' },
  { icon: 'fa-solid fa-users',                label: 'Clientes' },
  { icon: 'fa-solid fa-user-tie',             label: 'Vendedores' },
  { icon: 'fa-solid fa-handshake',            label: 'Comercial' },
  { icon: 'fa-solid fa-truck-fast',           label: 'Activos' },
  { icon: 'fa-solid fa-shield-halved',        label: 'Seguridad' },
  { icon: 'fa-solid fa-scale-balanced',       label: 'Riesgo' },
  { icon: 'fa-solid fa-robot',                label: 'IA' },
  { icon: 'fa-solid fa-comments',             label: 'Omnicanal' },
  { icon: 'fa-solid fa-mobile-screen-button', label: 'Mobile' },
  { icon: 'fa-solid fa-gears',                label: 'Operación' },
  { icon: 'fa-solid fa-plug',                 label: 'Integración' },
  { icon: 'fa-solid fa-diagram-project',      label: 'Flujo' },
  { icon: 'fa-solid fa-folder-open',          label: 'Documentos' },
  { icon: 'fa-solid fa-bell',                 label: 'Notificaciones' }
]

/** Situación comercial. Solo la ve Admin. */
export const COMMERCIAL_STATUS_META: Record<CommercialStatus, { label: string; color: string }> = {
  draft:    { label: 'Borrador',  color: '#94a3b8' },
  quoted:   { label: 'Cotizado',  color: '#f59e0b' },
  approved: { label: 'Aprobado',  color: '#3B82F6' },
  invoiced: { label: 'Facturado', color: '#00C781' }
}
